/**
 * ADR-0121 Slice 3 — import error / reason-code projection.
 *
 * The server's `validateRules` (services/api/src/sites/policy-rules-validate.ts)
 * is the single source of truth for what's publishable; on a 400 it returns a
 * short reason code like `rule_2_r05_no_caps_configured` or `too_many_rules`.
 * This file maps those codes to merchant-facing strings.
 *
 * Unknown codes are NEVER swallowed — `mapReason` falls through to a literal
 * `{ kind: 'unknown', label: code }` so a future server-side reason still
 * surfaces verbatim.
 *
 * Pure functions only — no I/O, no React imports.
 */
import { isIso4217 } from '@sill/types';
import type { Rule, RuleAction, RuleCategory, RuleType } from '@sill/types';

export interface ReasonProjection {
  /** `known` — we mapped this code; `unknown` — fall-through, show verbatim. */
  kind: 'known' | 'unknown';
  /** Merchant-facing one-liner. */
  label: string;
  /** Optional follow-up hint ("fix it by …"). */
  hint?: string;
  /** When the reason was for a specific rule index, this is parsed out. */
  rule_index?: number;
}

// ---------------------------------------------------------------------------
// Per-rule-type → human label tables.
// ---------------------------------------------------------------------------

/**
 * Top-level reasons (no `rule_<i>_` prefix). These are the all-of-array
 * checks that fire before the per-rule loop.
 */
const TOP_LEVEL_REASONS: Record<string, { label: string; hint?: string }> = {
  rules_must_be_array: {
    label: 'Expected a `rules` array (or `{ rules: […] }`).',
    hint: 'The file must be a JSON array of rules, or an object wrapping one under `rules`.',
  },
  too_many_rules: {
    label: 'Policy has too many rules.',
    hint: 'A site policy can hold at most 64 rules.',
  },
  version_malformed: {
    label: 'The policy version label contains invalid characters.',
    hint: 'Reload the page to let the dashboard generate a clean version label, then publish again.',
  },
  // r01 publish-time footgun guard (2026-09-23 vbout.com fix) — the
  // server's typed 400 reasons for the registry check. `r01_unknown_agent_ids`
  // additionally carries the offending list on the response envelope,
  // which the Rules view surfaces inline on the r01 field itself; the
  // generic banner here is the fallback copy for the toast / error
  // strip when the inline guard is bypassed.
  r01_empty_allowlist: {
    label: 'r01: the allowlist is empty.',
    hint: 'Add at least one agent_id. An empty allowlist on an enabled r01 rejects every real agent.',
  },
  r01_unknown_agent_ids: {
    label: 'r01: one or more agent ids are not recognized.',
    hint: 'Pick ids from the suggestions, or ones that have already placed or tried orders on this site.',
  },
};

/**
 * Per-rule reason fragments (after the `rule_<i>_` prefix is stripped).
 * Keys are the LITERAL fragment the server emits.
 */
const PER_RULE_FRAGMENT_REASONS: Record<string, { label: string; hint?: string }> = {
  not_object: { label: 'Rule is not an object.' },
  id_malformed: {
    label: 'Rule id is malformed.',
    hint: 'rule_id must be 1–128 chars, A–Z, a–z, 0–9, _ or - only.',
  },
  type_unknown: { label: 'Unknown rule type.' },
  type_unsupported: {
    label: 'This rule type has no evaluator handler yet.',
    hint: 'Pick a shipped rule (the catalog marks the rest "coming soon").',
  },
  category_unknown: { label: 'Unknown category.' },
  action_unknown: {
    label: 'action_on_match must be one of: reject, escalate, allow.',
  },
  order_not_integer: { label: '`order` must be an integer.' },
  enabled_not_boolean: { label: '`enabled` must be a boolean.' },
  params_shape: {
    label: 'params is missing or its `type` does not match the rule\'s type.',
  },
  params_too_large: {
    label: 'params JSON exceeds the per-rule 8 KiB limit.',
  },
  display_name_too_long: { label: '`display_name` is too long (max 256 chars).' },
  description_too_long: { label: '`description` is too long (max 256 chars).' },
  // r01 allowlist
  allowlist_empty_or_not_array: {
    label: 'r01: `allowlisted_agent_ids` must be a non-empty array.',
  },
  allowlist_too_long: { label: 'r01: allowlist exceeds 256 agents.' },
  allowlist_entry_malformed: {
    label: 'r01: an entry is not a valid agent_id.',
    hint: 'Format: agent_<lowercase letters/digits/underscores>, e.g. agent_openai_chatgpt_user.',
  },
  // r05 caps
  max_amount_usd_not_non_negative_number: {
    label: 'r05: max_amount_usd must be a non-negative number.',
  },
  caps_not_object: { label: 'r05: `caps` must be a `{ CCY: amount }` object.' },
  caps_currency_not_iso4217: {
    label: 'r05: a cap currency is not an ISO-4217 code.',
  },
  caps_amount_not_non_negative_number: {
    label: 'r05: a cap amount must be a non-negative number.',
  },
  on_unlisted_currency_invalid: {
    label: 'r05: `on_unlisted_currency` must be "reject" or "allow".',
  },
  r05_no_caps_configured: {
    label: 'r05: no caps configured.',
    hint: 'Add at least one currency + amount, or set `max_amount_usd`.',
  },
  // r06
  daily_cap_usd_not_non_negative_number: {
    label: 'r06: `daily_cap_usd` must be a non-negative number.',
  },
  // r31 (ADR-0151) — cross-agent org budget cap. Mirrors r06's reason
  // shape; `window_seconds` is the optional rolling-window field, and
  // `max_distinct_agents` is RESERVED for v2 (publish REJECTS a non-empty
  // value so we never silently accept unenforced config).
  org_budget_usd_not_non_negative_number: {
    label: 'r31: `org_budget_usd` must be a non-negative number.',
  },
  window_seconds_out_of_range: {
    label: 'r31: `window_seconds` must be an integer between 3600 (1h) and 2592000 (30d).',
  },
  max_distinct_agents_reserved_for_v2: {
    label: 'r31: `max_distinct_agents` is reserved for a future version — leave it unset.',
    hint: 'v1 enforces the USD budget only. The agent-count cap will land in a later release.',
  },
  // r07 auto-approve caps
  max_auto_approve_usd_not_non_negative_number: {
    label: 'r07: `max_auto_approve_usd` must be a non-negative number.',
  },
  auto_approve_caps_not_object: {
    label: 'r07: `auto_approve_caps` must be a `{ CCY: amount }` object.',
  },
  auto_approve_caps_currency_not_iso4217: {
    label: 'r07: a ceiling currency is not an ISO-4217 code.',
  },
  auto_approve_caps_amount_not_non_negative_number: {
    label: 'r07: a ceiling amount must be a non-negative number.',
  },
  r07_no_caps_configured: {
    label: 'r07: no auto-approve ceilings configured.',
    hint: 'Add at least one currency + ceiling, or set `max_auto_approve_usd`.',
  },
  // r12 geofence
  r12_mode_must_be_allow_or_deny: {
    label: 'r12: `mode` must be "allow" or "deny".',
  },
  r12_countries_empty_or_not_array: {
    label: 'r12: `countries` must be a non-empty array.',
  },
  r12_countries_too_long: {
    label: 'r12: too many countries (cap is 300).',
  },
  r12_country_not_iso_alpha2: {
    label: 'r12: a country code is not 2-letter ISO 3166-1 alpha-2.',
  },
  // r18 manifest hashes
  r18_manifest_hashes_empty_or_not_array: {
    label: 'r18: `manifest_hashes` must be a non-empty array.',
  },
  r18_manifest_hashes_too_long: {
    label: 'r18: too many approved digests (cap is 64).',
  },
  r18_manifest_hash_not_sha256_hex: {
    label: 'r18: a manifest hash is not 64-char lowercase hex SHA-256.',
  },
  // r23 / r25
  max_validity_seconds_not_positive_integer: {
    label: 'r23: `max_validity_seconds` must be a positive integer.',
  },
  max_body_bytes_not_positive_integer: {
    label: 'r25: `max_body_bytes` must be a positive integer.',
  },
  // r29 delegation chain
  r29_require_chain_not_boolean: {
    label: 'r29: `require_chain_when_delegated` must be a boolean.',
  },
  r29_max_chain_depth_out_of_range: {
    label: 'r29: `max_chain_depth` must be an integer between 1 and 4.',
  },
  r29_allowed_delegators_empty_or_not_array: {
    label: 'r29: `allowed_delegators` must be a non-empty array.',
  },
  r29_allowed_delegators_too_long: {
    label: 'r29: too many allowed delegators (cap is 64).',
  },
  r29_allowed_delegator_not_agent_id: {
    label: 'r29: an allowed delegator is not a valid agent id.',
  },
  // r_custom
  expression_empty_or_not_string: {
    label: 'r_custom: `expression` is empty or not a string.',
  },
  expression_too_large: {
    label: 'r_custom: expression exceeds the 4 KiB cap.',
  },
  expression_parse_failed: {
    label: 'r_custom: expression did not parse.',
  },
  // c01..c04 — catalog-eligibility rules
  c01_applies_to_invalid: {
    label: 'c01: `applies_to` must be "search", "checkout", or "both".',
  },
  c01_floors_minor_not_object: {
    label: 'c01: `floors_minor` must be a `{ CCY: integer-minor }` object.',
  },
  c01_no_floors_configured: { label: 'c01: no floors configured.' },
  c01_too_many_floors: { label: 'c01: too many floors (cap is 64).' },
  c01_floor_currency_not_iso4217: {
    label: 'c01: a floor currency is not ISO-4217.',
  },
  c01_floor_not_non_negative_integer: {
    label: 'c01: a floor amount must be a non-negative integer in minor units.',
  },
  c02_applies_to_invalid: {
    label: 'c02: `applies_to` must be "search", "checkout", or "both".',
  },
  c02_allowed_empty_or_not_array: {
    label: 'c02: `allowed` must be a non-empty array of availability states.',
  },
  c02_allowed_too_long: { label: 'c02: too many allowed states (cap is 64).' },
  c02_availability_value_unknown: {
    label: 'c02: an availability value is unknown.',
  },
  c03_applies_to_invalid: {
    label: 'c03: `applies_to` must be "search", "checkout", or "both".',
  },
  c03_min_quantity_not_non_negative_integer: {
    label: 'c03: `min_quantity` must be a non-negative integer.',
  },
  c04_applies_to_invalid: {
    label: 'c04: `applies_to` must be "search", "checkout", or "both".',
  },
  c04_mode_must_be_allow_or_deny: {
    label: 'c04: `mode` must be "allow" or "deny".',
  },
  c04_countries_empty_or_not_array: {
    label: 'c04: `countries` must be a non-empty array.',
  },
  c04_countries_too_long: {
    label: 'c04: too many countries (cap is 300).',
  },
  c04_country_not_iso_alpha2: {
    label: 'c04: a country code is not 2-letter ISO 3166-1 alpha-2.',
  },
};

/**
 * Best-effort prefix match for r_custom DSL parse errors. The server emits
 * codes like `rule_2_expression_<dsl_code>` — anything we don't have a
 * specific entry for falls through here.
 */
function expressionPrefixHint(fragment: string): string | undefined {
  if (fragment.startsWith('expression_forbidden_field_')) {
    return 'The DSL only allows reads from `mandate.*` paths (intent, principal, protocol_context).';
  }
  if (fragment.startsWith('expression_')) {
    return 'The custom expression failed to parse — check operators, quoted strings, and field paths.';
  }
  return undefined;
}

/**
 * Map a server reason code (or a top-level one) to a merchant-facing
 * projection. Pure function — handles both the `rule_<i>_<fragment>` shape
 * and the top-level `rules_must_be_array` / `too_many_rules` codes.
 */
export function mapReason(code: string): ReasonProjection {
  // 1) Top-level codes.
  const topLevel = TOP_LEVEL_REASONS[code];
  if (topLevel !== undefined) {
    return { kind: 'known', label: topLevel.label, ...(topLevel.hint !== undefined ? { hint: topLevel.hint } : {}) };
  }
  // 2) Per-rule codes: rule_<i>_<fragment>
  const m = /^rule_(\d+)_(.+)$/.exec(code);
  if (m !== null) {
    const idx = Number(m[1]);
    const fragment = m[2]!;
    const direct = PER_RULE_FRAGMENT_REASONS[fragment];
    if (direct !== undefined) {
      return {
        kind: 'known',
        label: `Rule ${idx + 1}: ${direct.label}`,
        ...(direct.hint !== undefined ? { hint: direct.hint } : {}),
        rule_index: idx,
      };
    }
    const exprHint = expressionPrefixHint(fragment);
    if (exprHint !== undefined) {
      return {
        kind: 'known',
        label: `Rule ${idx + 1}: ${fragment}`,
        hint: exprHint,
        rule_index: idx,
      };
    }
    // Unknown fragment — show verbatim, preserving the rule index.
    return { kind: 'unknown', label: code, rule_index: idx };
  }
  // 3) Truly unknown — pass verbatim.
  return { kind: 'unknown', label: code };
}

// ---------------------------------------------------------------------------
// Client-side validation mirror (UX only — the server's `validateRules`
// is the authority and runs again on POST/PUT).
// ---------------------------------------------------------------------------

const RULE_ID_RE = /^[A-Za-z0-9_-]{1,128}$/;
const MAX_RULES = 64;

/**
 * Quick structural mirror for the import preview pane. Reuses the same
 * reason-code vocabulary as the server so the merchant sees the same
 * label on a 200-preview as on a 400-response.
 *
 * Deliberately incomplete vs the server's per-rule param check (it does
 * not re-parse r_custom expressions client-side — only the server +
 * `@sill/policy` parser is authoritative). The "all-or-nothing" import
 * semantics still hold: a single failing rule reports here and the
 * server confirms on submit.
 */
export type RuleLike = Pick<Rule, 'rule_id' | 'type' | 'category' | 'params' | 'action_on_match' | 'order' | 'enabled'> & {
  display_name?: string;
  description?: string;
};

export function previewValidateRules(
  input: unknown,
): { ok: true; rules: RuleLike[] } | { ok: false; reason: string } {
  if (!Array.isArray(input)) {
    return { ok: false, reason: 'rules_must_be_array' };
  }
  if (input.length > MAX_RULES) {
    return { ok: false, reason: 'too_many_rules' };
  }
  const out: RuleLike[] = [];
  for (let i = 0; i < input.length; i += 1) {
    const r = input[i] as Record<string, unknown> | null;
    if (r === null || typeof r !== 'object' || Array.isArray(r)) {
      return { ok: false, reason: `rule_${i}_not_object` };
    }
    if (typeof r['rule_id'] !== 'string' || !RULE_ID_RE.test(r['rule_id'] as string)) {
      return { ok: false, reason: `rule_${i}_id_malformed` };
    }
    if (typeof r['type'] !== 'string') {
      return { ok: false, reason: `rule_${i}_type_unknown` };
    }
    if (typeof r['category'] !== 'string') {
      return { ok: false, reason: `rule_${i}_category_unknown` };
    }
    const action = r['action_on_match'];
    if (action !== 'reject' && action !== 'escalate' && action !== 'allow') {
      return { ok: false, reason: `rule_${i}_action_unknown` };
    }
    if (typeof r['order'] !== 'number' || !Number.isInteger(r['order'])) {
      return { ok: false, reason: `rule_${i}_order_not_integer` };
    }
    if (typeof r['enabled'] !== 'boolean') {
      return { ok: false, reason: `rule_${i}_enabled_not_boolean` };
    }
    const params = r['params'];
    if (
      params === null
      || typeof params !== 'object'
      || Array.isArray(params)
      || (params as { type?: unknown }).type !== r['type']
    ) {
      return { ok: false, reason: `rule_${i}_params_shape` };
    }
    // Light per-type spot-checks — server is authoritative for the rest.
    const t = r['type'] as string;
    const p = params as Record<string, unknown>;
    if (t === 'r05') {
      const caps = p['caps'];
      const max_amount = p['max_amount_usd'];
      const hasCaps =
        typeof caps === 'object' && caps !== null && !Array.isArray(caps) && Object.keys(caps).length > 0;
      const hasMaxAmount =
        typeof max_amount === 'number' && Number.isFinite(max_amount) && max_amount >= 0;
      if (!hasCaps && !hasMaxAmount) {
        return { ok: false, reason: `rule_${i}_r05_no_caps_configured` };
      }
      if (hasCaps) {
        for (const [ccy] of Object.entries(caps as Record<string, unknown>)) {
          if (!isIso4217(ccy)) {
            return { ok: false, reason: `rule_${i}_caps_currency_not_iso4217` };
          }
        }
      }
    }
    if (t === 'r_custom') {
      const expr = p['expression'];
      if (typeof expr !== 'string' || expr.trim().length === 0) {
        return { ok: false, reason: `rule_${i}_expression_empty_or_not_string` };
      }
    }
    out.push({
      rule_id: r['rule_id'] as Rule['rule_id'],
      type: r['type'] as RuleType,
      category: r['category'] as RuleCategory,
      params: params as Rule['params'],
      action_on_match: action as RuleAction,
      order: r['order'] as number,
      enabled: r['enabled'] as boolean,
      ...(typeof r['display_name'] === 'string' ? { display_name: r['display_name'] as string } : {}),
      ...(typeof r['description'] === 'string' ? { description: r['description'] as string } : {}),
    });
  }
  return { ok: true, rules: out };
}
